import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/utils';
// hover:brightness-200
const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-base text-[13px] font-medium ring-offset-background transition-all-300 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground hover:bg-black',
                active:
                    'text-primary-foreground rounded-sm bg-active hover:opacity-80',
                destructive:
                    'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                outline:
                    'border border-active text-active bg-background hover:bg-accent hover:text-active',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                ghost:
                    'hover:bg-accent hover:text-accent-foreground',
                link:
                    'text-primary underline-offset-4 hover:underline',
                submit:
                    'bg-background-5 text-primary-foreground hover:shadow-shadow-2',
                green:
                    'bg-green-1 text-primary-foreground hover:shadow-shadow-2',
                red:
                    'bg-red-1 text-primary-foreground hover:shadow-shadow-2',
                cancel:
                    'bg-background-3 text-background-5 border border-border-outline hover:shadow-shadow-2',
                report: 'border border-red-5 text-red-5',
                input:
                    'bg-background-input px-3 py-2 border border-border-2 rounded-base text-sm font-normal text-text-color hover:border-primary disabled:cursor-not-allowed disabled:opacity-1 disabled:bg-disable disabled:border-disable disabled:pointer-events-auto',
                gradient: 'bg-gradient-to-l from-[#2BB51C] to-[#34A853] text-primary-foreground hover:bg-black'
            },
            size: {
                default: 'h-12 px-2',
                sm: 'h-9 px-3',
                lg: 'h-11 w-36 px-1',
                xl: 'h-12 w-52 px-1',
                icon: 'h-9 w-9',
                input: 'h-10 w-full'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
